<script setup lang="ts">
import { useRoute } from 'vue-router';
import Divider from '@/components/common/divider.vue';
import MenuItem from '@/components/menu/common/item.vue';

/**
 * 移行期間中は既存のメニューと項目が重複する。
 * ANDEV_10131_CAPABILITY の削除時は、このメニューと情報収集エージェントの設定メニューに
 * 置き換わる menu/sections/admin-menu.vue と menu/sections/settings-menu.vue を削除する。
 */

withDefaults(
  defineProps<{
    isAdmin: boolean;
    isUserDocumentEnabled: boolean;
    hasUserDocumentNotification?: boolean;
  }>(),
  {
    hasUserDocumentNotification: false,
  },
);

const route = useRoute();

// 一覧から遷移する詳細・編集画面でも、元の項目を選択状態にする
const isUnder = (pathPrefix: string) =>
  route.path === pathPrefix || route.path.startsWith(`${pathPrefix}/`);

// 組織タグの画面は、いまいるセクションによって/settings配下と/admin配下のどちらにも遷移する
const isOrganizationTagSelected = () =>
  isUnder('/settings/organization_tags') || isUnder('/admin/organization_tags');
</script>

<template>
  <nav class="aconnect-settings-menu">
    <section class="menu-section">
      <div class="c-title c-title--s">全メンバー共通の設定</div>
      <ul class="menu-items">
        <li>
          <MenuItem
            id="organization_tags"
            name="組織タグ管理"
            :route="{ name: 'settingsOrganizationTags' }"
            :is-selected="isOrganizationTagSelected()"
          />
        </li>
      </ul>
    </section>

    <template v-if="isAdmin">
      <Divider />
      <section class="menu-section">
        <div class="c-title c-title--s">管理者の設定</div>
        <ul class="menu-items">
          <li>
            <MenuItem
              id="contracts"
              name="契約・クレジット管理"
              :route="{ name: 'contracts' }"
              :is-selected="isUnder('/admin/contracts')"
            />
          </li>
          <li>
            <MenuItem
              id="members"
              name="メンバー管理"
              :route="{ name: 'members' }"
            />
          </li>
          <li>
            <MenuItem
              id="dashboard"
              name="レポート"
              :route="{ name: 'dashboardHome' }"
              :is-external-link="true"
            />
          </li>
          <li v-if="isUserDocumentEnabled">
            <MenuItem
              id="user_documents"
              name="社内情報ストレージ管理"
              :route="{ name: 'anewsStorageIndex' }"
              :is-selected="isUnder('/admin/user_documents')"
              :has-notification="hasUserDocumentNotification"
            />
          </li>
          <li>
            <MenuItem
              id="ip_address"
              name="IPアドレス制限"
              :route="{ name: 'ipAddressRestrictions' }"
            />
          </li>
          <li>
            <MenuItem
              id="sso"
              name="SSO認証"
              :route="{ name: 'samlFederationList' }"
              :is-selected="isUnder('/admin/sso')"
            />
          </li>
        </ul>
      </section>
    </template>
  </nav>
</template>

<style lang="scss" scoped>
.aconnect-settings-menu {
  display: flex;
  flex-direction: column;
  gap: 24px;
  box-sizing: border-box;
  width: 240px;
  height: 100%;
  padding: 24px 0;
  overflow-y: auto;
  background: $color-white;
  border-right: 1px solid $color-gray400;
  --menu-item-width: 100%;
}

.menu-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 16px;
}

.menu-items {
  list-style: none;
  padding: 0;
  margin: 0;
}
</style>
